import { type FunctionComponent, type ReactNode } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { Stack, Text } from '@atlaskit/primitives';
import SectionMessage, {
  SectionMessageAction,
} from '@atlaskit/section-message';

const impactMessages = defineMessages({
  impactTitle: {
    id: 'app.updates.impact.banner.title',
    defaultMessage: 'Understand change impact at a glance',
    description:
      'Title for the impact rating banner on the app updates list page',
  },
  impactBody: {
    id: 'app.updates.impact.banner.body',
    defaultMessage:
      'Use the new <strong>Impact</strong> rating to quickly identify how much attention a change may need. Filter release notes by Low, Medium, or High impact to focus on the changes that matter most to your organization.',
    description:
      'Introductory text for the impact rating banner on the app updates list page',
  },
  betaDisclaimer: {
    id: 'app.updates.impact.banner.beta.disclaimer',
    defaultMessage:
      '<strong>Impact ratings are currently in beta.</strong> We’re continuing to improve their accuracy and would love your feedback to help us fine-tune the experience.',
    description:
      'Beta disclaimer for impact ratings on the app updates list page',
  },
  impactLink: {
    id: 'app.updates.impact.banner.link',
    defaultMessage: 'Learn more and share feedback',
    description:
      'Link text for the impact rating banner on the app updates list page',
  },
});

const releaseNotesMessages = defineMessages({
  title: {
    id: 'app.updates.launch.banner.title',
    defaultMessage: 'New ways to stay on top of release notes',
    description: 'Title for the launch banner on the app updates list page',
  },
  body: {
    id: 'app.updates.launch.banner.body',
    defaultMessage:
      'Quickly catch up on recent changes with the <strong>New this week</strong> filter and unread indicators, or switch to <strong>Release tracks changes</strong> to review what’s included in upcoming Preview and Bundled releases.',
    description: 'Body text for the launch banner on the app updates list page',
  },
  link: {
    id: 'app.updates.launch.banner.link',
    defaultMessage: 'Learn more on Community',
    description: 'Link text for the launch banner on the app updates list page',
  },
});

const IMPACT_RATING_COMMUNITY_POST_URL =
  'https://community.atlassian.com/forums/Community-Announcements-articles/A-smarter-way-to-understand-which-Atlassian-Cloud-changes-need/ba-p/3274946';

const RELEASE_NOTES_LAUNCH_POST_URL =
  'https://community.atlassian.com/forums/Community-Announcements-articles/Two-new-ways-to-stay-on-top-of-Atlassian-Cloud-release-notes/ba-p/3252956';

export const BetaBanner: FunctionComponent = () => {
  const { formatMessage } = useIntl();

  return (
    <SectionMessage
      appearance="discovery"
      title={formatMessage(impactMessages.impactTitle)}
      actions={[
        <SectionMessageAction
          href={IMPACT_RATING_COMMUNITY_POST_URL}
          target="_blank"
        >
          {formatMessage(impactMessages.impactLink)}
        </SectionMessageAction>,
      ]}
    >
      <Stack space="space.100">
        <Text>
          {formatMessage(impactMessages.impactBody, {
            strong: (chunks: ReactNode) => (
              <Text as="span" weight="bold">
                {chunks}
              </Text>
            ),
          })}
        </Text>
        <Text>
          {formatMessage(impactMessages.betaDisclaimer, {
            strong: (chunks: ReactNode) => (
              <Text as="span" weight="bold">
                {chunks}
              </Text>
            ),
          })}
        </Text>
      </Stack>
    </SectionMessage>
  );
};

export const ReleaseNotesBanner: FunctionComponent = () => {
  const { formatMessage } = useIntl();

  return (
    <SectionMessage
      appearance="discovery"
      title={formatMessage(releaseNotesMessages.title)}
      actions={[
        <SectionMessageAction
          href={RELEASE_NOTES_LAUNCH_POST_URL}
          target="_blank"
        >
          {formatMessage(releaseNotesMessages.link)}
        </SectionMessageAction>,
      ]}
    >
      <Text>
        {formatMessage(releaseNotesMessages.body, {
          strong: (chunks: ReactNode) => (
            <Text as="span" weight="bold">
              {chunks}
            </Text>
          ),
        })}
      </Text>
    </SectionMessage>
  );
};

export default BetaBanner;
